// Story 7.1 (ADO-904): the product card, first ported to the designer prototype (Epic 7
// re-skin, ADO-903) and laid out since Pricing migration 18 (ADO-2249) as Figma's v9 card,
// grid and list (the "Layouts (v9)" note below). Promo is a PLACEHOLDER sample value (see
// productCardSample): the read model doesn't carry it yet. The RRP and the margin are the
// samples too while `VITE_REAL_PRICES_ENABLED` is off, and the real ones when it is on (below).
// The footer's unit price (Figma 4002:14960 desktop, 4002:20873 mobile) is rendered by the shared
// `ProductPrice` (Pricing migration 17, ADO-2248): with `VITE_REAL_PRICES_ENABLED` off it is the
// stand-in `provisionalUnitPrice`, as before; on, it is the price quotes endpoint's figure, and the
// RSP in the meta line is the real one too (left out when the product has none). Story 11.10
// (ADO-1961), S-9: with the flag on, the margin is the price quote's (`useProductMargin`): `Margin
// x.xx%`, or `Margin —` (spoken "not available") when the product is priced but the margin cannot
// be computed; left out while the price is loading, failed or unpriced, as the RSP is; and no label
// at all where the store does not see its margin. When the RSP and the margin are both left out,
// the grid keeps its fixed-height row and the list drops the `·` before `Case`. With the flag off
// the sample margin shows for every account, as the sample RSP does (S-7). Pricing migration 18
// (ADO-2249): the footer price is `CardPrice`, row 17's price wrapped so a promotion's price is red
// with its case was price struck above it, a deposit shows under it, and a promotion or contract
// price is read out in words (DEC-15). Unchanged with the flag off.
//
// All Story 7.3/7.4/7.5/7.9/7.11 behaviour is preserved (availability pill +
// "{n} in store", Brand Bank image + placeholder, favourite heart, one-click add
// with inline confirm, substitutes CTA + panel). See the store wiring below —
// the markup was re-skinned, not the logic.
//
// Story 7.12 Iteration 2 (ADO-911):
//   • The interim card-hosted `RecommendationsSection` is RETIRED (OQ-12). The
//     PO-approved PDP redesign gave the rails their permanent home — the
//     "Often purchased together" carousel on `/products/:sku` — so the card no
//     longer hosts them. `RecommendationRail`, `useRecommendationsStore` and
//     `recommendations.client.ts` are all kept and reused there.
//   • The product NAME is now a link to that PDP (DR-1), handing the already-
//     rendered read-model row over in router state so the PDP needs no re-fetch.
//   • The Add button passes the product name to `useAddConfirmation`, which makes
//     add-from-listing an upsell trigger (AC7 — the modal fires on ALL entry points).
//
// Layouts (Pricing migration 18, ADO-2249, Task 4: "Product cards · v9 proposal (22 Oct)",
// DEC-11 and DEC-12, Figma sets 1 and 2): `grid` (default) is v9's 419px card "for speed": the
// top strip (in basket or on order) or the top row with the heart, the badge row, the image,
// the code row, the name and the offer slot, then `RSP · Margin`, `Case`, a divider and the
// footer. `list` is v9's row "for detail": the decision line (heart, state, offer, then RSP,
// margin and case), then the image, the name and the codes line, and the price and action
// right. The not-available search card keeps its own markup (`NotAvailableProductCard`).

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type FocusEvent } from 'react';
import { Link } from 'react-router';
import {
  Button,
  ConflictMessage,
  Icon,
  PickedFreshPill,
  Popover,
  QuantityStepper,
  type QuantityStepperCommit,
} from '../../design-system';
import type { ProductSummary } from '../../api/browse.client';
import type { SearchResultProduct } from '../../api/search.client';
import type { AvailabilityBand } from '../../api/availability.client';
import { useAvailabilityStore } from '../../availability/useAvailabilityStore';
import { useCandidateAvailability } from '../../availability/useCandidateAvailability';
import { PICKED_FRESH_LABEL, showsPickedFresh } from '../../availability/pickedFresh';
import { useSessionDepotId } from '../../availability/session-depot';
import { useCatalogueEntryKey } from '../../ordering/catalogueEntry';
import { atDepot } from '../../availability/depot-scoped-entry';
import { useFavouritesStore } from '../../favourites/useFavouritesStore';
import { useBasketStore } from '../../basket/useBasketStore';
import { rbpCartLabel } from '../../basket/rbpCartMarker';
import { RbpNprPill } from '../../basket/RbpNprPill';
import { useIsNarrowViewport } from '../../basket/useIsNarrowViewport';
import { useAddConfirmation } from '../../basket/useAddConfirmation';
import {
  isAtLineMaximum,
  sanitiseQuantityInput,
  typedLineQuantity,
} from '../../basket/lineQuantity';
import type { ConflictIntent } from '../../basket/conflictCopy';
import { useLineConflictMessage } from '../../basket/useLineConflictMessage';
import type { UpsellVariant } from '../../upsell/useUpsellModalStore';
import { useSubstitutesStore } from '../../substitutes/useSubstitutesStore';
import { useSessionStore } from '../../auth/useSessionStore';
import { selectShowsStockQuantity } from '../../availability/showsStockQuantity';
import type { ProductDetailHandover } from '../ProductDetail/ProductDetail';
import { StockCardContent } from '../ProductDetail/StockCardContent';
import { useStockCardPopoverStore } from '../../stock-card/useStockCardPopoverStore';
import { nameTooltipPlacement, type NameTooltipPlacement } from './nameTooltipPlacement';
import { useTextTruncated } from './useTextTruncated';
import { useOnOrderFor } from '../../onOrder/useOnOrder';
import { OnOrderChip } from '../../onOrder/OnOrderChip';
import { onOrderChipText } from '../../onOrder/onOrderDisplay';
import { ProductImage } from './ProductImage';
import { SubstitutesPanel } from './SubstitutesPanel';
import { NotAvailableProductCard } from './NotAvailableProductCard';
import { RbpLinksCta } from './RbpLinksCta';
import { showsRbpLinks } from '../../rbp/rbpLinksOffered';
import { sampleCardData } from './productCardSample';
import { usePriceBlocksAdd, useProductMargin, useProductRrp } from '../../pricing/ProductPrice';
import { EmptyValue } from '../../shared/EmptyValue';
import { CardPrice } from '../../pricing/CardPrice';
import { ContractPricePill } from '../../pricing/ContractPricePill';
import { gridOfferText, isPromotion, listOfferText } from '../../pricing/priceExplanation';
import { realPricesEnabled } from '../../pricing/realPrices';
import { useListedProducts } from '../../pricing/listVisibility';
import type { SubstituteProduct } from '../../api/substitutes.client';
import { usePriceEntry, usePriceStore } from '../../pricing/usePriceStore';
import { useMultibuyDealsEntry } from '../../multibuy/useMultibuyDealsStore';
import {
  cardDeal,
  compactDealText,
  wideDealText,
  withMoreOffers,
} from '../../multibuy/multibuyDealText';
import { compactStripName, seeDealName } from '../../multibuy/spokenDealText';
import { useDealDialogStore } from '../../multibuy/useDealDialogStore';
import styles from './ProductCard.module.css';

/** Story 7.30 (ADO-1112), AC2: the one label a Leaflet Line is identified by, everywhere. */
export const LEAFLET_LINE_LABEL = 'Leaflet Line';

/** Story 7.32 (ADO-1115), AC1: the label a Featured Line is identified by. */
export const FEATURED_LINE_LABEL = 'Featured Line';

export interface ProductCardProps {
  /** Story 8.2 (ADO-2011), AC6: a search row may carry `notAvailable: true` (a
   *  discontinued exact hit), which renders the `NotAvailableProductCard` instead. */
  readonly product: ProductSummary | SearchResultProduct;
  /** Story 7.5 (AC4b): the product's account-type eligibility. Supplied on the
   *  /favourites screen; when omitted (browse/search) no AC4b gating applies. */
  readonly accountTypeEligible?: readonly string[];
  /** Story 7-FU-D (ADO-1217), AC3 — above-the-fold priority, forwarded to
   *  `ProductImage` for the first grid row (the LCP candidate). */
  readonly priority?: boolean;
  /** Epic 7 re-skin (ADO-903) — card layout. `grid` (default) is the vertical
   *  card; `list` reflows to the horizontal row variant (prototype view toggle). */
  readonly layout?: 'grid' | 'list';
  /** Story 7.12 Iteration 2 (ADO-911), AC7 — suppress the Linked Product upsell
   *  on this card's Add. Set by the upsell modal itself, whose cards are also
   *  ProductCards: without it, adding a linked product would stack a second
   *  modal on the first. Every other surface leaves it off. */
  readonly suppressUpsell?: boolean;
  /** Story 7.12 (ADO-911) design refresh — which upsell presentation this card's
   *  Add opens. `'toast'` (default) is the non-invasive listing/search popup;
   *  `'modal'` is the full PDP dialog. The PDP "Often purchased together" carousel
   *  mounts this card with `'modal'` so an add from a PDP tile matches a PDP hero
   *  add; every listing/search surface takes the default. Inert when
   *  `suppressUpsell` is set (the upsell's own tiles). */
  readonly upsellVariant?: UpsellVariant;
  /** Story 7.12 Iteration 2 (ADO-911), DR-4 / D-1 — hide the placeholder
   *  `RSP · Margin` figures (the grid keeps their row, blank; the list shows only the case). Set by
   *  the recommendation surfaces (the PDP carousel and the Linked Product modal), whose tiles
   *  carry no RRP or margin: neither has a source. The footer's unit price still shows. The
   *  listing grid/list surfaces leave it off. */
  readonly hideRrpAndMargin?: boolean;
  /** Story 7.12 Iteration 2 (ADO-911), DR-1 — declare that `product` is a
   *  recommendation STUB (`toProductSummary(...)`: no `description`, no
   *  `unitOfMeasure`) rather than a read-model row. Set by the same two
   *  recommendation surfaces as `hideRrpAndMargin`. It rides the PDP handover
   *  in router state so `useProductDetailStore` can mark the entry `partial` and
   *  still resolve the real row — without it, a cold-cache visit from a tile
   *  pinned the stub as `loaded` for the rest of the session. */
  readonly partialRow?: boolean;
  /** The grid card's name row. Left unset it hugs the name below 768px (one card per row,
   *  so the whole name shows). `'two-lines'` reserves two name lines at every width, for
   *  side-by-side tiles (the recommendation rail) whose rows must line up; a longer name
   *  still shows in full below 768px. */
  readonly nameRow?: 'two-lines';
  /** True on the /favourites screen. Forwarded to the not-available card so an off-sale
   *  favourite keeps a remove-favourite control. */
  readonly favouritesView?: boolean;
}

/** Band → stock-pill label. Meaning is carried by the label text, not colour. */
const BAND_LABEL: Record<AvailabilityBand, string> = {
  IN_STOCK: 'In Stock',
  LOW_STOCK: 'Low Stock',
  OUT_OF_STOCK: 'Out of Stock',
};
/** The pill for a supplier-UNAVAILABLE (blocked) product, the prototype's wording. */
const PRODUCT_NOT_AVAILABLE_LABEL = 'Product not available';
const BAND_CLASS: Record<AvailabilityBand, string> = {
  IN_STOCK: styles.stockIn,
  LOW_STOCK: styles.stockLow,
  OUT_OF_STOCK: styles.stockOut,
};

// Story 7-FU-I (ADO-1222), AC2 — the ≤3 DISPLAY cap now lives on the CLIENT and is
// applied AFTER the OOS-exclusion (the server over-fetches candidates so the panel
// can still fill up to 3 once out-of-stock substitutes are dropped). The display cap
// lives here on the client — the web deliberately does not import backend constants.
//
// INVARIANT (cross-layer): the server's over-fetch ceiling `MAX_SUBSTITUTE_CANDIDATES`
// (get-substitutes.service.ts, currently 10) MUST stay ≥ this cap, or the panel can
// under-deliver after OOS-exclusion — the precise regression AC2 exists to fix.
//
// It cannot be TYPED across the module boundary (the web deliberately does not import
// backend constants), but it can be TESTED, and prose on both sides was not enough: with the
// invariant asserted nowhere, dropping the server ceiling to 2 kept every spec in this story
// green. So each side now asserts the relationship against a literal naming the other —
// `ProductCard.substitutes.spec.tsx` here, `get-substitutes.service.spec.ts` there. EXPORTED
// for exactly that reason: an unexported cap is one no spec on either side can reference.
export const MAX_SUBSTITUTES_DISPLAYED = 3;

const NO_SUBSTITUTES: readonly SubstituteProduct[] = [];
const substituteSku = (substitute: SubstituteProduct): string => substitute.sku;

/**
 * Story 8.2 (ADO-2011), AC6 — a `notAvailable` search row (a discontinued product an
 * exact code / EAN / TUC hit surfaced) renders the not-available card: no Add, no
 * quantity, no stock, no favourite, no product link. So does an off-sale RBP core parent a
 * listing shows for its RBP links (`rbpLinks: 'OFFERED'`, sent for an off-sale product only).
 * Every other row is the ordinary orderable card below.
 */
export function ProductCard(props: ProductCardProps) {
  if (
    ('notAvailable' in props.product && props.product.notAvailable === true) ||
    props.product.rbpLinks === 'OFFERED'
  ) {
    return (
      <NotAvailableProductCard
        product={props.product}
        priority={props.priority ?? false}
        layout={props.layout ?? 'grid'}
        favouritesView={props.favouritesView ?? false}
      />
    );
  }
  return <OrderableProductCard {...props} />;
}

function OrderableProductCard({
  product,
  accountTypeEligible,
  priority = false,
  layout = 'grid',
  suppressUpsell = false,
  hideRrpAndMargin = false,
  partialRow = false,
  upsellVariant = 'toast',
  nameRow,
}: ProductCardProps) {
  const packLine = [product.packSize, product.unitOfMeasure].filter(Boolean).join(' · ');
  const sample = sampleCardData(product.sku);
  const realPrices = realPricesEnabled();
  // ADO-2248 AC9: the real RRP with the flag on (null while loading, when unpriced or failed,
  // and when the product has none), the sample RRP with it off (ADO-2248 D1). v9 labels it RSP
  // (ADO-2249 DEC-12 item 8).
  const realRrp = useProductRrp(product.sku);
  const rrp = realPrices ? realRrp : sample.rrp;
  // Story 11.10 (ADO-1961), S-7 and S-9: the quote's margin with the flag on, the sample off.
  const realMargin = useProductMargin(product.sku);
  // ADO-2249 (Figma sets 1 and 4): with the flag on and a price to show, a promotion's offer
  // goes in the offer slot and its RSP is red; a contract's `Contract price` pill takes the slot.
  const priceEntry = usePriceEntry(product.sku);
  // ADO-2248 PS-2: Add waits for the price (loading, failed, couldn't be priced), with main's
  // disabled look; the stepper of a product already in the cart stays enabled.
  const priceBlocksAdd = usePriceBlocksAdd(product.sku);
  const priced =
    realPrices && priceEntry?.state === 'priced' && priceEntry.unitPrice.amount > 0
      ? priceEntry
      : null;
  const promotion = priced !== null && isPromotion(priced);
  const contract = priced?.kind === 'contract';
  const offerText =
    priced === null || !promotion
      ? null
      : layout === 'list'
        ? listOfferText(priced)
        : gridOfferText(priced);
  // Pricing migration 32 (ADO-3985), T11: the product's live multi-buy deal, with the flag on
  // only (D-5). With a promotion and a multi-buy, the multi-buy takes the strip and the promotion
  // counts in "+N more" (R4 + R7 01/02, DQ-7); the `Contract price` pill keeps its slot (DQ-12).
  // The deal shows only once the price has answered (priced or unpriced), and a promotion's strip
  // waits until the deals have answered or failed, including before the page has asked for them
  // (it asks after its first paint), so the strip never swaps under the shopper. While the deal data is
  // loading, failed or absent, no deal shows (AC6). One deal per card, the one `cardDeal` picks,
  // plus "+N more" for the others (DQ-4).
  const dealsEntry = useMultibuyDealsEntry(product.sku);
  const priceAnswered = priceEntry?.state === 'priced' || priceEntry?.state === 'unpriced';
  const liveDeals = realPrices && dealsEntry?.state === 'ready' ? dealsEntry.deals : [];
  const shownDeal = priceAnswered && !contract ? cardDeal(liveDeals) : null;
  const moreOffers = shownDeal === null ? 0 : liveDeals.length - 1 + (promotion ? 1 : 0);
  const dealsUnsettled =
    realPrices && dealsEntry?.state !== 'ready' && dealsEntry?.state !== 'failed';
  const openDeal = () => {
    if (shownDeal !== null)
      useDealDialogStore
        .getState()
        .openDeal({ skuCode: product.sku, offerCode: shownDeal.multibuyOfferCode });
  };
  const availability = useAvailabilityStore((s) => s.entries[product.sku]);
  const band = availability?.band;
  // RBP-D33: the server says the product has RBP links where SL1.0 runs its live stock check;
  // the card completes the check with the price and stock it already shows.
  const rbpPriceEntry = usePriceStore((s) => s.entries[product.sku]);
  const offersRbpLinks = showsRbpLinks(product.rbpLinks, {
    availability,
    price: rbpPriceEntry,
    isPickedFresh: product.isPickedFresh,
  });
  const rbpLinksRow = offersRbpLinks ? (
    <RbpLinksCta sku={product.sku} name={product.name} layout={layout} />
  ) : null;

  const isFavourite = useFavouritesStore((s) => s.favouriteSkus.has(product.sku));
  const favouritePending = useFavouritesStore((s) => s.pending.has(product.sku));
  const addFavourite = useFavouritesStore((s) => s.add);
  const removeFavourite = useFavouritesStore((s) => s.remove);
  const favouriteError = useFavouritesStore((s) =>
    s.mutationError?.sku === product.sku ? s.mutationError.message : null,
  );

  // Story 7.9 (AC1/AC2/AC3) — one-click add to basket.
  const basketPending = useBasketStore((s) => s.pending.has(product.sku));
  const basketError = useBasketStore((s) => s.mutationErrors.get(product.sku) ?? null);
  const supplierUnavailable = availability?.status === 'UNAVAILABLE';
  // What the session depot's cart holds for this product. The card shows Add while it
  // holds none and the cart line's own stepper once it holds some, never both (PO
  // decision 2026-10-02; no Figma frame draws these two states).
  const setLineQuantity = useBasketStore((s) => s.setQuantity);
  const inCart = useBasketStore((s) => s.quantityBySku.get(product.sku) ?? 0);
  // Story 18.18 (ADO-4037): the cart line's RBP Shopping List part, for the cart's "RBP NPR item"
  // pill. A boolean, so the card re-renders only when the mark itself changes.
  const fromRbpList = useBasketStore((s) => {
    const line = s.lines.find((candidate) => candidate.skuCode === product.sku);
    return line !== undefined && rbpCartLabel(line) !== null;
  });
  // True from an Add press until that add settles. The store shows the new line
  // optimistically at once, so without this the card would swap to the stepper and,
  // on a failed add, back to Add; it keeps a disabled Add on screen instead.
  const [firstAddInFlight, setFirstAddInFlight] = useState(false);
  // What the status region says after a live push swapped the action under the Shopper's
  // focus (see the swap effect below). An add clears it, so once the add confirmation's
  // window ends the region falls silent instead of repeating a stale swap.
  const [actionSwapAnnouncement, setActionSwapAnnouncement] = useState('');
  const showsCartStepper = inCart > 0 && !firstAddInFlight;
  // Story 8.12 (ADO-2021) — loaded by the listing's batch read (`useOnOrder`), read per SKU here.
  const onOrder = useOnOrderFor(product.sku);

  // AC4b (Story 7.5) — account-type ineligibility.
  const accountType = useSessionStore((s) => s.accountType);
  // An EMPTY eligibility list means "not yet restricted", NOT "eligible for nobody":
  // the eligibility feed is dormant today (Story 7.13 D-2 — `account_type_eligible` is
  // empty everywhere until Epic 14), and the canonical server gate `isAccountTypeEligible`
  // fails OPEN on an empty list. This check must mirror that, or every favourite whose
  // eligibility is still empty is wrongly badged "Not on account" while the same product
  // browses and orders fine (its listing card, and the server, treat empty as unrestricted).
  const notOnAccount =
    accountTypeEligible !== undefined &&
    accountTypeEligible.length > 0 &&
    accountType !== null &&
    !accountTypeEligible.includes(accountType);

  // Substitutes are fetched wherever the Substitute CTA can render, which is only on a
  // blocked card (see `offersSubstitute` below): supplier UNAVAILABLE or not on account.
  // The advisory OUT_OF_STOCK band alone leaves the product orderable, so that card
  // offers Add and fetches nothing.
  const loadSubstitutes = useSubstitutesStore((s) => s.load);
  const retainSubstitutes = useSubstitutesStore((s) => s.retain);
  const releaseSubstitutes = useSubstitutesStore((s) => s.release);
  const rawSubstitutesEntry = useSubstitutesStore((s) => s.bySku[product.sku]);

  // Subscribe to the session depot (NOT a bare getState()) so the effects below re-run on a
  // depot switch. Resolved ABOVE the substitute-load effect because the depot is a REAL
  // input to that fetch (`fetchSubstitutes` is depot-scoped): for a card whose trigger
  // doesn't toggle across a switch — an in-stock NOT-on-account card — nothing else in the
  // dep list moves, so without it `load()` would never re-fire and the card would sit on a
  // dead CTA until it remounted. Same reason `useAvailability` keys on the active depot.
  const sessionDepotId = useSessionDepotId();
  const catalogueEntryKey = useCatalogueEntryKey();

  // Story 8.1 (ADO-1092), AC1 — the depot-selection gate: with no active session
  // depot, Add is blocked (a basket is scoped to a depot; the header shows the
  // "Select a depot" prompt). Availability is also off in this state, so the whole
  // card sits in a "pick a depot first" posture. Defence-in-depth: `useBasketStore.add`
  // re-checks and no-ops with `NO_DEPOT_SELECTED_MESSAGE`.
  const noDepotSelected = sessionDepotId === null;

  // A candidate list is only valid for the depot it was fetched at: `findSubstituteLinks`
  // dedupes depot-specific-wins, so one depot's candidates can include products not
  // configured as substitutes at another. Treating a depot-mismatched entry as NOT-yet-
  // loaded holds the panel closed until the refetch for THIS depot lands, structurally —
  // rather than resting on the availability gate's timing, which a candidate that already
  // has an entry at the new depot (also in the grid, re-read by its batch) can short out.
  const substitutesEntry = atDepot(rawSubstitutesEntry, sessionDepotId);

  // Whether the store holds a settled ANSWER for this card's own SKU at the active depot.
  // The gap-bridge below needs it because an absent entry means two things: "not read yet"
  // and "read, and there is no stock row at this depot".
  const availabilityChecked = useAvailabilityStore((s) => s.checkedSkus.has(product.sku));

  // The D-4 trigger, HELD through an availability gap. A depot switch wipes the availability
  // store's entries for the previous depot, so this card's own `availability` — and with it
  // `supplierUnavailable` — goes momentarily UNKNOWN before the new depot's batch lands. Retracting
  // the trigger there tore the substitute work down mid-flight: releasing the last claim
  // drops the `loading` entry (killing the `refetching` hold, so the CTA unmounted anyway)
  // and bumps the cancel generation, throwing away the refetch `load()` had just issued and
  // fetching the list a second time once the band came back.
  // Unknown stock is not "in stock": while we hold ANY substitute state for this SKU the
  // trigger stays engaged until availability returns and genuinely says otherwise.
  //
  // `!availabilityChecked` is the bridge's SETTLE CONDITION, and it is what makes this a
  // bridge rather than a latch. "Momentarily unknown" has to end: a read that comes back with
  // no row for this product — ordinary at a depot that does not stock it — leaves
  // `availability` undefined FOREVER, and the entry it is paired with here survives the
  // session (`release` drops only `loading` entries). Without a settle condition the trigger
  // stayed engaged for the rest of the session, offering a Substitute CTA with no stock pill
  // on the strength of a read that had answered "not stocked here".
  const inAvailabilityGap =
    availability === undefined && !availabilityChecked && rawSubstitutesEntry !== undefined;
  const shouldLoadSubstitutes = supplierUnavailable || notOnAccount || inAvailabilityGap;
  // The action the card last committed (updated by the swap effect below), so the gap
  // holds Substitute only on a card already offering it: a fresh card whose SKU merely has
  // an earlier substitutes entry offers Add until a read says otherwise.
  const offeredSubstituteRef = useRef(false);

  useEffect(() => {
    if (!shouldLoadSubstitutes) return;
    // Claim the SKU's substitute work BEFORE asking for it, and drop the claim when this card
    // unmounts or its trigger turns off. The store cancels a pending auto-retry only on the
    // LAST release, so a scheduled retry never fires for a SKU no card is showing while a
    // second card still displaying the same SKU keeps its request and its loading state. The
    // per-SKU attempt count is retained across the claim, so the bound survives a remount.
    const sku = product.sku;
    retainSubstitutes(sku);
    void loadSubstitutes(sku);
    return () => releaseSubstitutes(sku);
    // `sessionDepotId`: re-load at the new depot on a live switch (see comment above).
    // `catalogueEntryKey`: an entry change resets the store (Enabler E7-C, ADO-2274, D-1), so
    // re-load the selected entry's candidates.
  }, [
    shouldLoadSubstitutes,
    product.sku,
    loadSubstitutes,
    retainSubstitutes,
    releaseSubstitutes,
    sessionDepotId,
    catalogueEntryKey,
  ]);

  // Story 7-FU-I (ADO-1222), AC1 — the RAW server candidates (account-type-eligible,
  // over-fetched). Their OWN availability has to be established for the OOS filter below to
  // stop being inert; keyed on the raw candidates, NOT the filtered list, which depends on
  // availability (that would be circular). The read, its tracking claim and its bounded
  // retry live in the availability seam — see `useCandidateAvailability`.
  const rawCandidateSkus =
    substitutesEntry?.phase === 'loaded' ? substitutesEntry.substitutes.map((s) => s.sku) : [];
  const candidateAvailability = useCandidateAvailability(rawCandidateSkus, sessionDepotId);

  // Exclude substitutes that are THEMSELVES out of stock (AC1), then cap the visible list at
  // 3 (AC2 — the cap the server used to apply now runs here, post-exclusion). With a depot
  // we require a POSITIVE established signal: an entry that is present AND orderable. A
  // missing entry is NOT read as in-stock — that closes both the failed-read hole and the
  // partial-response hole (a SKU the API didn't return is dropped, not offered as
  // available). A picked-fresh candidate is the exception: it has no stock state and is always
  // orderable (Enabler E7-B, ADO-2273; MP AVL-5.4), so the read leaves it out and it is offered
  // once the read has settled (`hasSubstitutes` waits on `established`). No depot → degrade,
  // offer candidates as-is.
  //
  // Pricing migration 17b (ADO-3948, DEC-3): the candidates' prices are asked for as soon as the
  // list lands, beside their availability, and a candidate with no price of its own is left out
  // before the cap, so its place goes to the next one. While their prices are arriving the card
  // offers no Substitute, so the button never shows and then disappears.
  const { shown: pricedCandidates, settling: substitutePricesSettling } = useListedProducts(
    substitutesEntry?.substitutes ?? NO_SUBSTITUTES,
    { skuOf: substituteSku },
  );
  const substitutes = pricedCandidates
    .filter((s) => {
      if (sessionDepotId === null) return true; // degrade: can't check → show candidates
      if (s.isPickedFresh) return true;
      const a = candidateAvailability.entries[s.sku];
      return a !== undefined && a.band !== 'OUT_OF_STOCK' && a.status !== 'UNAVAILABLE';
    })
    .slice(0, MAX_SUBSTITUTES_DISPLAYED);
  const hasSubstitutes =
    substitutesEntry?.phase === 'loaded' &&
    candidateAvailability.established &&
    !substitutePricesSettling &&
    substitutes.length > 0;

  const [panelOpen, setPanelOpen] = useState(false);
  /** Story 7.16 (ADO-1373) — the Stock Card popover's open state. Independent of
   *  `panelOpen`: the two affordances sit on different rows of the card and the
   *  design does not couple them. Coordinated ACROSS cards through
   *  `useStockCardPopoverStore` (code review follow-up) rather than local state, so
   *  opening one card's popover closes any other card's — the grid's implicit "one
   *  open at a time" assumption is now enforced, not just assumed. */
  const stockCardOpen = useStockCardPopoverStore((s) => s.openSku === product.sku);
  const openStockCardPopover = useStockCardPopoverStore((s) => s.open);
  const closeStockCardPopover = useStockCardPopoverStore((s) => s.close);
  const substituteTriggerRef = useRef<HTMLDivElement>(null);
  const productLinkRef = useRef<HTMLAnchorElement>(null);
  // The grid card clamps the name to two lines from 768px; when that cuts it off, a tooltip
  // under the name shows it in full on hover or keyboard focus. It is hidden from assistive
  // tech, which already reads the whole name from the link.
  const [nameRef, nameTruncated] = useTextTruncated<HTMLHeadingElement>(product.name);
  // It opens under the name unless the viewport has no room there, then over it; measured as
  // the pointer arrives or focus lands, before the tooltip shows, so it never jumps sides.
  const nameTooltipRef = useRef<HTMLSpanElement>(null);
  const [nameTooltipSide, setNameTooltipSide] = useState<NameTooltipPlacement>('below');
  const placeNameTooltip = (event: { readonly currentTarget: HTMLElement }) => {
    const tooltip = nameTooltipRef.current;
    if (tooltip === null) return;
    setNameTooltipSide(
      nameTooltipPlacement(
        event.currentTarget.getBoundingClientRect(),
        tooltip.offsetHeight,
        window.innerHeight,
      ),
    );
  };
  const openSubstitutes = () => setPanelOpen(true);
  const closeSubstitutes = useCallback(() => {
    setPanelOpen(false);
    const button = substituteTriggerRef.current?.querySelector('button');
    // TWO paths, not three. `preventScroll` on both: a plain focus() scrolls the card's
    // overflow-hidden box while the sheet is still sliding, which reads as a jump.
    //
    // Whenever this runs the CTA is either mounted and focusable, or gone — there is no
    // mounted-but-natively-disabled case to fall back to the wrapper for. A native `disabled`
    // needs `substituteCtaTitle === undefined` while the CTA renders, and both legs of the
    // footer now render it only under `showSubstituteCta`, whose four disjuncts each imply a
    // title (or, for `hasSubstitutes`, that the button is enabled outright).
    if (button) button.focus({ preventScroll: true });
    // The trigger can be GONE by the time we get here: a live availability push that takes the
    // last in-stock substitute away flips `hasSubstitutes` false, which unmounts the CTA in the
    // SAME commit whose effect calls this — so the ref reads null and focus would fall to
    // <body>, stranding a keyboard user mid-card. Land on the card title link instead: it is
    // rendered in both layouts, never conditional, and names the product, so the move is
    // announced (WCAG 2.4.3 focus order).
    else productLinkRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (panelOpen && !hasSubstitutes) closeSubstitutes();
  }, [panelOpen, hasSubstitutes, closeSubstitutes]);

  // Story 7.12 Iteration 2 (AC7) — passing the name makes add-from-listing an
  // upsell trigger, alongside the PDP hero, quick-add and the carousel tiles.
  // `upsellVariant` routes the presentation: listing/search cards default to the
  // toast; the PDP carousel passes `'modal'` so a PDP-tile add matches the hero.
  const { confirmed, confirm } = useAddConfirmation(product.sku, {
    productName: product.name,
    upsellVariant,
    upsell: !suppressUpsell,
  });

  // Swapping Add for the stepper (and back) unmounts the focused control, which would
  // drop focus to <body>. A swap asks for focus on a control by its `data-control`; it is
  // taken as soon as that control is on screen (this render or a later one).
  const addBlockRef = useRef<HTMLDivElement>(null);
  const pendingFocusRef = useRef<string | null>(null);
  // Whether keyboard focus is on the card's action (Add, the stepper or Substitute), and
  // whether the card is about to unmount the focused action itself (a removal, or a push
  // that swaps the action). Chrome fires a blur with no related target as the focused
  // control is removed, which looks like a click on blank space; while an unmount is
  // expected that blur keeps `focusOnActionRef`, so focus can still be handed on. Focus
  // landing on the action, leaving it for another element, or the hand-off running
  // clears the expectation.
  const focusOnActionRef = useRef(false);
  const expectingActionUnmountRef = useRef(false);
  const focusAddBlockControl = (control: string): boolean => {
    const target = addBlockRef.current?.querySelector<HTMLElement>(`[data-control="${control}"]`);
    if (!target) return false;
    // `preventScroll`: the hand-off lands a round trip after the tap, by which time the
    // Shopper may have scrolled on; a plain focus() would jump the page back to the card.
    target.focus({ preventScroll: true });
    // A disabled control (Add while the removal is still in flight) refuses focus, so
    // the request stays open until a render shows it enabled.
    return document.activeElement === target;
  };
  /** Whether the Shopper has left focus on this card's controls (or it dropped to
   *  <body> when the focused one unmounted), so a swap may move it. */
  const focusStayedOnTheControls = () =>
    document.activeElement === document.body ||
    addBlockRef.current?.contains(document.activeElement) === true;

  const requestFocus = (control: string) => {
    pendingFocusRef.current = focusAddBlockControl(control) ? null : control;
  };
  useLayoutEffect(() => {
    const control = pendingFocusRef.current;
    if (control === null) return;
    // The Shopper moved focus elsewhere meanwhile: the request lapses.
    if (!focusStayedOnTheControls()) {
      pendingFocusRef.current = null;
      expectingActionUnmountRef.current = false;
    } else if (focusAddBlockControl(control)) pendingFocusRef.current = null;
  });

  // One press adds one. When it lands the stepper replaces Add, and focus follows to
  // its Increase if it was still on Add. Add is disabled while the add is in flight,
  // which drops its focus to <body>, so a failed add hands focus back to Add.
  const addOne = () => {
    setFirstAddInFlight(true);
    setActionSwapAnnouncement('');
    void addAndFocus(1);
  };
  // Story 8.3 (ADO-2012), D-19: an add refused because a colleague added the product first
  // leaves their line in the cart, so the stepper replaces Add as a landed add does, and
  // focus follows to it in the same way.
  const addAndFocus = (quantity: number) =>
    confirm(quantity)
      .then((landed) => {
        if (!focusStayedOnTheControls()) return;
        const swapsToStepper =
          landed || (useBasketStore.getState().quantityBySku.get(product.sku) ?? 0) > 0;
        pendingFocusRef.current = swapsToStepper ? 'increase' : 'add';
        // The stepper replaces Add under the Shopper's focus.
        if (swapsToStepper) expectingActionUnmountRef.current = true;
      })
      .finally(() => setFirstAddInFlight(false));

  // The stepper edits the cart line through the same store actions, and by the same
  // rules, as the cart row (`CartLineRow`): no save action, never disabled while an edit
  // is in flight, decrease at 1 and a typed 0 remove the line, an emptied field restores
  // the quantity and saves nothing. A removal returns the card to Add and focus with
  // it; if the removal does not land the stepper is back, and so is focus.
  const removeLine = async (control: string) => {
    expectingActionUnmountRef.current = true;
    const landed = setLineQuantity(product.sku, 0);
    requestFocus('add');
    if (await landed) return;
    pendingFocusRef.current = null;
    if (focusStayedOnTheControls()) requestFocus(control);
    else expectingActionUnmountRef.current = false;
  };
  const decreaseLine = () => {
    if (inCart <= 1) void removeLine('decrease');
    else void setLineQuantity(product.sku, inCart - 1);
  };
  const increaseLine = () => void setLineQuantity(product.sku, inCart + 1);
  const commitTypedLine = ({ typed, trigger, focusTarget }: QuantityStepperCommit) => {
    const next = typedLineQuantity(typed, inCart);
    if (next === null) return;
    if (next > 0) {
      void setLineQuantity(product.sku, next);
      return;
    }
    const focusLeftTheControl =
      trigger === 'blur' && focusTarget !== null && !addBlockRef.current?.contains(focusTarget);
    if (focusLeftTheControl) void setLineQuantity(product.sku, 0);
    else void removeLine('quantity');
  };

  // Story 8.3 (ADO-2012): the Conflict Message's action is a new change to the line, which
  // ends the message (the lifetime rule). Focus goes where the same change from the card's
  // own controls would send it.
  const runConflictAction = (intent: ConflictIntent) => {
    if (intent.type === 'remove') {
      void removeLine('decrease');
      return;
    }
    if (intent.type === 'add') {
      setFirstAddInFlight(true);
      setActionSwapAnnouncement('');
      requestFocus('add');
      void addAndFocus(intent.quantity);
      return;
    }
    requestFocus('quantity');
    void setLineQuantity(product.sku, intent.quantity);
  };

  const toggleFavourite = () => {
    if (isFavourite) void removeFavourite(product.sku);
    else void addFavourite(product.sku);
  };

  const substitutePhase = substitutesEntry?.phase;
  // While a loaded candidate set's availability is still being checked, the CTA reads as
  // "loading" — never "error", and never the silent no-CTA end state a CHECKED empty result
  // gets (the AC4 labelling split; see `substituteCtaTitle`).
  const substituteAvailabilityPending =
    substitutePhase === 'loaded' && candidateAvailability.pending;
  // A depot switch is refetching a candidate list this card was ALREADY offering (the store
  // stamps `refetching` for exactly that case). Hold the CTA — pending, non-actionable —
  // through the round trip instead of flashing it away and back: this card had one on
  // screen, and the switch is a user action whose result is a moment away. A FIRST load
  // carries no flag, so a card with nothing on screen yet still shows nothing until it
  // resolves.
  const substituteRefetching =
    substitutePhase === 'loading' && substitutesEntry?.refetching === true;
  // A failed availability read is NOT an empty result: it keeps the CTA and labels it an
  // error, where a checked-empty one drops the CTA entirely — so this card never implies
  // there are none when we simply couldn't check their stock (the AC1 hole).
  const substituteCtaError = substitutePhase === 'error' || candidateAvailability.failed;
  // TWO status labels, not three. AC4 also names a "No substitutes available" label for a
  // `loaded` candidate set that OOS-exclusion emptied, but AC3's PO decision (2026-07-23 — "if
  // no substitutes available, do not show them") supersedes that clause: such a card renders no
  // Substitute CTA at all, on either footer leg, so there is no control left to hang the copy
  // on. Concretely, that state fails every disjunct of `showSubstituteCta` below — `loaded`
  // excludes `substituteRefetching`, and the other three are exactly the cases handled here —
  // which is also why every RENDERED non-actionable CTA carries a title, and so stays
  // `aria-disabled` and focusable rather than natively `disabled`. The trailing `undefined` is
  // the ternary's else, for the states in which no CTA renders.
  const substituteCtaTitle = hasSubstitutes
    ? undefined
    : substitutePhase === 'loading' || substituteAvailabilityPending
      ? 'Loading substitutes…'
      : substituteCtaError
        ? 'Substitutes could not be loaded'
        : undefined;

  const substituteCtaBlocked = !hasSubstitutes;
  // A native `disabled` button drops out of the tab order, so its `aria-describedby` is
  // never announced — and with AC4 dropping the manual retry CTA this status IS the entire
  // error UX. When there's a status to convey, keep the control FOCUSABLE
  // via `aria-disabled` (guarding the click below) so a keyboard / screen-reader user
  // reaches the description on focus; fall back to a plain inert `disabled` only when there
  // is nothing to say. The status text lives in a visually-hidden span the button points
  // at — NOT a per-card live region: an availability outage across a grid would otherwise
  // mount one `role=alert` per card and interrupt the screen reader once for each, and the
  // happy path would announce "Loading substitutes…" per card. On focus is enough.
  const substituteCtaAriaDisabled = substituteCtaBlocked && substituteCtaTitle !== undefined;
  const substituteStatusId = `substitute-status-${product.sku}`;
  const substituteCta = (
    <div className={styles.substituteCta} ref={substituteTriggerRef} tabIndex={-1}>
      <Button
        variant="substitute"
        className={styles.cta}
        onClick={substituteCtaBlocked ? undefined : openSubstitutes}
        disabled={substituteCtaBlocked && !substituteCtaAriaDisabled}
        aria-disabled={substituteCtaAriaDisabled || undefined}
        aria-haspopup="dialog"
        aria-expanded={panelOpen}
        aria-describedby={substituteCtaTitle ? substituteStatusId : undefined}
        title={substituteCtaTitle}
      >
        Substitute
      </Button>
      {substituteCtaTitle && (
        <span id={substituteStatusId} className={styles.visuallyHidden}>
          {substituteCtaTitle}
        </span>
      )}
    </div>
  );

  // Enabler E7-B (ADO-2273), AC15 restated by D-15: the count shows only where the active
  // entry shows a quantity (`showsStockQuantity`); elsewhere the caption is empty and the pill
  // beside it carries the band.
  const showsQuantity = useSessionStore(selectShowsStockQuantity);
  const showStockDetail = availability !== undefined && showsQuantity;

  // ── Shared fragments (identical wiring in both layouts) ──

  // Story 7.12 Iteration 2 (DR-1) — "view product": the card title links to the
  // PDP and hands over the read-model row it already rendered, so the PDP mounts
  // with real data and no second request. A REAL <Link> (not a click handler) so
  // the crumb is middle-clickable, openable in a new tab and copyable — and so a
  // shared URL still resolves via the PDP's own deep-link path.
  //
  // `partial` travels with the row so the PDP knows whether it received a full
  // read-model row or a recommendation stub it must still resolve (see
  // `partialRow` above). Omitted unless true, so listing handovers are unchanged.
  // `catalogueEntryKey` names the entry the row was read under: the PDP seeds it only while
  // that entry is still selected (Enabler E7-C, ADO-2274, D-1).
  // `productLinkRef`: the substitute panel's focus-return fallback when its own trigger has
  // been unmounted by a live availability push (see `closeSubstitutes`). Only ONE layout
  // renders at a time, so a single ref is unambiguous.
  const productLink = (className: string) => (
    <Link
      ref={productLinkRef}
      className={className}
      to={`/products/${encodeURIComponent(product.sku)}`}
      state={
        (partialRow
          ? { product, partial: true, catalogueEntryKey }
          : { product, catalogueEntryKey }) satisfies ProductDetailHandover
      }
    >
      {product.name}
    </Link>
  );

  // Favourite heart: v9's chip-less 22px heart, at the right end of the grid card's top strip
  // or top row and first on the list card's decision line (Figma set 2). A 44px button (FR100)
  // whose hit area hangs below its row, inside the clipped card.
  const heartButton = (
    <button
      type="button"
      className={styles.heart}
      data-favourited={isFavourite}
      aria-pressed={isFavourite}
      disabled={favouritePending}
      aria-label={
        isFavourite ? `Remove ${product.name} from favourites` : `Add ${product.name} to favourites`
      }
      onClick={toggleFavourite}
    >
      <Icon name="heart" size={22} />
    </button>
  );

  // v9's badge row (DEC-12 item 1): `Not on account` as today. No Multi-buy badge: the sample one
  // is deleted and the v9 cards draw none (ADO-3985 T10, DQ-10). No "Substitutes Available"
  // badge (2026-07-17 demo feedback).
  // Story 7.30 (ADO-1112), AC2: the Leaflet Line marker, from the server's `offerKinds` fact.
  // The card is the ONE place that renders it, and the fact is the SAME `search.offers` row the
  // `offerType=LEAFLET_LINE` collection filters on, so a Shopper can tell a Leaflet Line inside
  // the collection and anywhere else in their Range, on the same basis. The route plays no part.
  const isLeafletLine = product.offerKinds?.includes('LEAFLET_LINE') ?? false;
  // Story 7.32 (ADO-1115), AC1: a Featured Line carries what BWG said about it and the account's
  // charge; the server sends `featuredLine` only for a line that is live, in range and priced.
  const featuredLine = product.featuredLine;
  // The RBP core mark, one per card (DF-28: "Core" wins over the link mark). Figma has no RBP
  // mark, so it takes the prototype's badge place with SL1.0's words and glyphs.
  const rbpBadge =
    product.rbpMark === undefined ? null : (
      <span className={styles.badgeRbp} data-rbp-mark={product.rbpMark}>
        <Icon
          name={product.rbpMark === 'CORE' ? 'rbpCore' : 'rbpLink'}
          size={14}
          title={product.rbpMark === 'CORE' ? 'Core' : 'RBP - Link'}
        />
        <span aria-hidden="true">{product.rbpMark === 'CORE' ? 'Core' : 'RBP - Link'}</span>
      </span>
    );
  // Story 18.18 (ADO-4037): the cart's "RBP NPR item" pill after the core mark, for a product
  // whose cart line the RBP Shopping List added, so the card shows what its cart line shows.
  const rbpNprBadge = fromRbpList ? <RbpNprPill className={styles.badgeRbp} /> : null;
  const hasBadges =
    notOnAccount ||
    isLeafletLine ||
    featuredLine !== undefined ||
    rbpBadge !== null ||
    rbpNprBadge !== null;
  const badgeEls = (
    <>
      {featuredLine !== undefined && !notOnAccount && (
        <span className={styles.badgeFeaturedLine}>{FEATURED_LINE_LABEL}</span>
      )}
      {isLeafletLine && !notOnAccount && (
        <span className={styles.badgeLeafletLine}>{LEAFLET_LINE_LABEL}</span>
      )}
      {notOnAccount && <span className={styles.badgeNeutral}>Not on account</span>}
      {rbpBadge}
      {rbpNprBadge}
    </>
  );

  // v9's state (DEC-12 item 9): `On order` when the product is on an open order, else
  // `In basket · N cases` while the cart holds it; on order and in the cart shows `On order`,
  // the stepper already saying how many are in the cart. The on-order text is the shared chip's,
  // unchanged (`onOrderChipText`). In the basket exactly when the stepper shows.
  const cardState: 'on-order' | 'in-basket' | undefined =
    onOrder !== undefined && onOrderChipText(onOrder) !== ''
      ? 'on-order'
      : showsCartStepper
        ? 'in-basket'
        : undefined;
  const stateText =
    cardState === 'on-order' && onOrder !== undefined ? (
      <OnOrderChip product={onOrder} className={styles.stateChip} />
    ) : cardState === 'in-basket' ? (
      <span className={styles.stateText} data-card-state="">
        <Icon name="cart" size={layout === 'list' ? 16 : 14} className={styles.stateIcon} />
        <span>{`In basket · ${inCart} ${inCart === 1 ? 'case' : 'cases'}`}</span>
      </span>
    ) : null;

  // The offer slot (Figma sets 1 and 4): a promotion's offer row, or the `Contract price` pill
  // (hidden from assistive tech: the price says "Contract price" in words, DEC-15).
  const priceOffer =
    shownDeal !== null || (promotion && dealsUnsettled) ? null : offerText !== null ? (
      <p className={styles.offerRow} data-offer-row="">
        <Icon name="tag" size={14} className={styles.offerIcon} />
        <span className={styles.offerText}>{offerText}</span>
      </p>
    ) : contract ? (
      <ContractPricePill aria-hidden="true" />
    ) : null;
  // Pricing migration 32 (ADO-3985), T11: the multi-buy strips (the confirmed Figma properties of
  // frames 01, 02, A, B and m/02). The compact one (grid card, phone list row) is the promotion
  // row's geometry and colours with a gift leading and an arrow trailing, the whole strip one
  // button that opens the deal's dialog. The wide one (list row from 768px) is the promotion pill
  // with a gift leading, then "See deal" styled as the row's Stock card link, the only button.
  // Each is named with the words it shows first (WCAG 2.5.3, T17).
  const compactText =
    shownDeal === null ? null : withMoreOffers(compactDealText(shownDeal), moreOffers);
  const compactDealStrip =
    shownDeal === null || compactText === null ? null : (
      <button
        type="button"
        className={`${styles.offerRow} ${styles.dealStrip}`}
        data-multibuy-strip="compact"
        aria-label={compactStripName(compactText, product.name)}
        onClick={openDeal}
      >
        <Icon name="gift" size={14} className={styles.offerIcon} />
        <span className={styles.dealText}>{compactText}</span>
        <Icon name="arrowRight" size={14} className={styles.offerIcon} />
      </button>
    );
  const wideDealStrip =
    shownDeal === null ? null : (
      <p className={`${styles.offerRow} ${styles.dealStripWide}`} data-multibuy-strip="wide">
        <Icon name="gift" size={14} className={styles.offerIcon} />
        <span className={styles.dealBody}>
          <span className={styles.dealText}>
            {withMoreOffers(wideDealText(shownDeal), moreOffers)}
          </span>
          <button
            type="button"
            className={`${styles.stockBtn} ${styles.seeDeal}`}
            aria-label={seeDealName(shownDeal, product.name)}
            onClick={openDeal}
          >
            See deal
            <Icon name="arrowRight" size={14} />
          </button>
        </span>
      </p>
    );
  const offerSlot = priceOffer ?? compactDealStrip;

  // `RSP · Margin` and `Case` (Figma set 2, DEC-12 items 8 and 13): muted labels, strong values,
  // a decorative dot between. The RSP is red for a promotion; no struck RSP before it (DEC-8).
  // The recommendation surfaces leave RSP and margin out (`hideRrpAndMargin`, DEC-12 item 7).
  const separator = (
    <span className={styles.metaDot} aria-hidden="true">
      ·
    </span>
  );
  const rspPart =
    rrp === null ? null : (
      <>
        <span className={styles.metaLabel}>RSP</span>{' '}
        <span
          className={promotion ? `${styles.metaValue} ${styles.promotion}` : styles.metaValue}
          data-rsp=""
        >
          {rrp}
        </span>
      </>
    );
  const marginValue = !realPrices ? (
    <span className={styles.metaValue}>{`${sample.marginPct}%`}</span>
  ) : realMargin.kind === 'shown' ? (
    <span className={styles.metaValue}>{realMargin.text}</span>
  ) : realMargin.kind === 'unavailable' ? (
    <EmptyValue spoken="not available" className={styles.metaValue} />
  ) : null;
  const marginPart =
    marginValue === null ? null : (
      <span data-margin="">
        <span className={styles.metaLabel}>Margin</span> {marginValue}
      </span>
    );
  const rspAndMargin =
    hideRrpAndMargin || (rspPart === null && marginPart === null) ? null : (
      <>
        {rspPart}
        {rspPart !== null && marginPart !== null && <> {separator} </>}
        {marginPart}
      </>
    );
  const caseValue =
    packLine === '' ? null : (
      <>
        <span className={styles.metaLabel}>Case</span>{' '}
        <span className={styles.metaValue}>{packLine}</span>
      </>
    );

  // A supplier-UNAVAILABLE product is blocked, and the pill says so in place of the band,
  // as the prototype's `outOfStock` state does (Ignite `stock-status-pill/index.tsx:25,87`,
  // "Product not available" on the error colours). It is the card's only statement of
  // the block: no message sits under the footer, so the footer lines up with its
  // neighbours'.
  //
  // A picked-fresh product has no availability entry (E7-B AVL-5.4, ADO-2273), so SL1.0's short
  // "Picked fresh" takes the pill's place (Enabler E7-C, ADO-2274; `stock_and_subs.php:35-41`),
  // drawn as the design system's PickedFreshPill (Figma 7298:215034). It never blocks the Add.
  const stockPill =
    band !== undefined ? (
      <span
        className={`${styles.stockPill} ${supplierUnavailable ? styles.stockOut : BAND_CLASS[band]}`}
      >
        <span className={styles.stockDot} aria-hidden />
        {supplierUnavailable ? PRODUCT_NOT_AVAILABLE_LABEL : BAND_LABEL[band]}
      </span>
    ) : (
      showsPickedFresh(product, availability) && (
        <PickedFreshPill>{PICKED_FRESH_LABEL}</PickedFreshPill>
      )
    );

  // The "N in store" teaser, on the list card only (designer decision, 2026-10-07, ADO-2011)
  // and only from 768px (PO decision, 2026-10-08).
  const stockTeaser = (
    <span className={styles.stockTeaser}>
      {showStockDetail && <span>{Math.max(0, availability.quantityOnHand)} in store</span>}
    </span>
  );
  // Story 7.16 (ADO-1373) part B / D-1 — the Stock button opens the SAME Stock
  // Card the PDP accordion shows (`StockCardContent`), in an anchored popover
  // (Figma 3581:10656 open over the grid, 3575:10456 the panel itself). It shipped
  // as a disabled "(coming soon)" affordance because gap G2 had no producer; Story
  // 14.15's EPOS ingest closed that.
  //
  // The interaction MIRRORS the Substitute panel in this same file rather than
  // inventing a second model: a boolean open state, Escape / outside-click /
  // close-button dismissal (owned by `Popover`), and focus returned to the trigger
  // here on close — with the same `productLinkRef` fallback for the case where the
  // trigger is no longer mounted. The one structural difference is placement: the
  // Substitutes sheet lives INSIDE `.card` (which is `overflow: hidden`), while the
  // approved design has this popover overlapping neighbouring cells, so it portals
  // out. See `design-system/Popover` for why neither existing surface could serve.
  const stockButtonRef = useRef<HTMLButtonElement>(null);
  const closeStockCard = useCallback(() => {
    closeStockCardPopover(product.sku);
    const trigger = stockButtonRef.current;
    if (trigger) trigger.focus({ preventScroll: true });
    else productLinkRef.current?.focus({ preventScroll: true });
  }, [closeStockCardPopover, product.sku]);
  // Only a list card at 768px and up renders the Stock card, so a card that stops showing
  // its stock row (the user toggles to the grid view, or a list card is narrowed below
  // 768px) takes its popover with it but would keep the store's slot, and reopen it on the
  // way back. Release the slot on that transition only: a card that never showed the row
  // never held it, so a grid card showing the same SKU must not close an open list card's
  // popover.
  const narrowViewport = useIsNarrowViewport();
  const showsStockRow = layout === 'list' && !narrowViewport;
  const previouslyShowedStockRowRef = useRef(showsStockRow);
  useEffect(() => {
    if (previouslyShowedStockRowRef.current && !showsStockRow) {
      closeStockCardPopover(product.sku);
    }
    previouslyShowedStockRowRef.current = showsStockRow;
  }, [showsStockRow, closeStockCardPopover, product.sku]);
  const stockButton = (
    <button
      type="button"
      ref={stockButtonRef}
      className={styles.stockBtn}
      aria-label={`Stock card for ${product.name}`}
      aria-haspopup="dialog"
      aria-expanded={stockCardOpen}
      onClick={() =>
        stockCardOpen ? closeStockCardPopover(product.sku) : openStockCardPopover(product.sku)
      }
    >
      Stock card
    </button>
  );
  // Mounted only while open — `Popover` portals to <body>, so a mounted-but-closed
  // panel would be a stray node per card on a 20-card grid, and `StockCardContent`
  // fires its read on mount, which is what keeps the read ON DEMAND (one open card
  // at a time) rather than eager per card.
  const stockCardPopover = stockCardOpen && (
    <Popover
      open={stockCardOpen}
      onClose={closeStockCard}
      title="Stock card"
      anchorRef={stockButtonRef}
    >
      <StockCardContent sku={product.sku} productName={product.name} />
    </Popover>
  );
  const addBlock = (
    <div className={styles.addBlock} ref={addBlockRef}>
      {showsCartStepper ? (
        <QuantityStepper
          className={styles.cartStepper}
          size="adaptive"
          value={inCart}
          label={`Cart quantity for ${product.name}`}
          decreaseLabel={`Decrease quantity in cart of ${product.name}`}
          increaseLabel={`Increase quantity in cart of ${product.name}`}
          onDecrease={decreaseLine}
          onIncrease={increaseLine}
          increaseDisabled={isAtLineMaximum(inCart)}
          busy={basketPending}
          decreaseControl="decrease"
          increaseControl="increase"
          editable={{
            inputLabel: `Quantity in cart of ${product.name}`,
            sanitiseInput: sanitiseQuantityInput,
            onCommit: commitTypedLine,
            control: 'quantity',
          }}
        />
      ) : (
        <Button
          variant="primary"
          className={styles.cta}
          data-control="add"
          onClick={addOne}
          disabled={
            basketPending ||
            firstAddInFlight ||
            supplierUnavailable ||
            noDepotSelected ||
            priceBlocksAdd
          }
          // Stable accessible name (matches the supplier-unavailable gate) so it is
          // the same control whether enabled or gated; the depot reason rides `title`
          // + the header "Select a depot" prompt is the primary CTA (Story 8.1 AC1).
          aria-label={`Add ${product.name} to basket`}
          title={noDepotSelected ? 'Select a depot to start your basket' : undefined}
        >
          Add
        </Button>
      )}
    </div>
  );

  // `hasSubstitutes` requires the candidates' availability to be ESTABLISHED, so gating the
  // CTA on it alone would render no Substitute CTA at all while that read is pending or has
  // FAILED, leaving the "Substitutes could not be loaded" status unreachable on exactly the
  // 7.11 AC2 case the CTA exists for. This keeps the CTA through the pending and error
  // states so the card can surface its status; the genuinely-empty result (`loaded`, no
  // candidates) is excluded, so that card shows no dead Substitute CTA (7.11 AC3).
  // `substituteRefetching` joins the union for the depot-switch case: a card that WAS
  // offering substitutes keeps its (pending) CTA while the new depot's list loads.
  const showSubstituteCta =
    hasSubstitutes || substituteAvailabilityPending || substituteCtaError || substituteRefetching;
  // One action per card, as the prototype's product card does (`primaryAction`, Ignite
  // `product-card/index.tsx:895-960`): an orderable card offers Add (or the cart stepper
  // once the product is in the cart) and never Substitute; a blocked card offers Substitute
  // and never Add or the stepper, even when the product is already in the cart (the cart
  // row is where that line is removed). Blocked is supplier UNAVAILABLE (the prototype's
  // `outOfStock`, "Product not available") or not on the account (`unavailableForAccount`).
  // The advisory OUT_OF_STOCK band with an AVAILABLE status stays orderable (DD-S.2; the
  // prototype's `temporarilyOut`, primary action "add"). A card already on Substitute
  // holds it through the availability gap, so a depot switch re-reading its stock does not
  // flash Add. Deliberate render-phase read: `offeredSubstituteRef` is the last committed
  // action.
  const offersSubstitute =
    supplierUnavailable || notOnAccount || (inAvailabilityGap && offeredSubstituteRef.current);

  // A live availability push can swap the action under the Shopper's focus (Add or the
  // stepper for Substitute, or back), unmounting the focused control and dropping focus to
  // <body>; `focusOnActionRef` and `expectingActionUnmountRef` (declared above) carry focus
  // through it.
  const ctaClusterRef = useRef<HTMLDivElement>(null);
  // Deliberate render-phase write: Chrome's removal blur fires while React commits the
  // swap, before any effect runs, so the expectation has to be set while rendering it.
  if (offersSubstitute !== offeredSubstituteRef.current) expectingActionUnmountRef.current = true;
  const trackActionFocus = () => {
    focusOnActionRef.current = true;
    expectingActionUnmountRef.current = false;
  };
  const trackActionBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (next !== null && event.currentTarget.contains(next)) return;
    if (next !== null || !expectingActionUnmountRef.current) {
      focusOnActionRef.current = false;
      expectingActionUnmountRef.current = false;
    }
  };
  // When the action swaps with focus on it, focus follows to the action that replaced it,
  // or to the card title when there is none (a blocked card with no substitute), the same
  // fallback `closeSubstitutes` uses. The status region says what changed, since the move
  // is not the Shopper's doing.
  useLayoutEffect(() => {
    if (offeredSubstituteRef.current === offersSubstitute) return;
    offeredSubstituteRef.current = offersSubstitute;
    // The hand-off below settles the swap either way.
    expectingActionUnmountRef.current = false;
    if (!focusOnActionRef.current) return;
    const cluster = ctaClusterRef.current;
    const active = document.activeElement;
    const focusWasDropped = active === null || active === document.body || !active.isConnected;
    // Focus already sits on the new action (some engines move it there themselves); any
    // other live element means the Shopper has moved on, so it is left alone.
    const focusOnNewAction = !focusWasDropped && cluster?.contains(active) === true;
    if (!focusWasDropped && !focusOnNewAction) return;
    if (focusWasDropped) {
      const replacement = cluster?.querySelector<HTMLElement>(
        offersSubstitute ? 'button' : '[data-control="add"], [data-control="increase"]',
      );
      replacement?.focus({ preventScroll: true });
      if (!replacement || document.activeElement !== replacement) {
        focusOnActionRef.current = false;
        productLinkRef.current?.focus({ preventScroll: true });
      }
    }
    setActionSwapAnnouncement(
      offersSubstitute
        ? `${product.name}: product not available`
        : `${product.name} can be added to your basket again`,
    );
  }, [offersSubstitute, product.name]);

  const featuredContent =
    featuredLine === undefined ? null : (
      <p className={styles.featuredLine} data-featured-line="">
        <span className={styles.featuredHeadline}>{featuredLine.headline}</span>
        {featuredLine.copy !== null && (
          <span className={styles.featuredCopy}>{featuredLine.copy}</span>
        )}
      </p>
    );

  const footer = (
    <div className={styles.footer}>
      <CardPrice
        sku={product.sku}
        productName={product.name}
        className={layout === 'list' ? `${styles.price} ${styles.listPrice}` : styles.price}
        align={layout === 'list' ? 'end' : 'start'}
        accountCharge={featuredLine?.unitPrice}
      />
      <div
        className={styles.ctaCluster}
        ref={ctaClusterRef}
        onFocus={trackActionFocus}
        onBlur={trackActionBlur}
      >
        {/* An empty cluster is the answer for a blocked card with no substitute to offer:
            the "Not on account" badge or the "Product not available" pill already says why. */}
        {offersSubstitute ? showSubstituteCta && substituteCta : addBlock}
      </div>
      {/* One polite region per card, outside the swap so it survives it: it says the cart
          total once when an add lands (the stepper replacing Add is silent), and what
          changed when a live push swaps the action under the Shopper's focus. */}
      <span className={styles.visuallyHidden} role="status">
        {confirmed ? `Added ${product.name} to basket, ${inCart} in cart` : actionSwapAnnouncement}
      </span>
    </div>
  );

  // Story 8.3 (ADO-2012), Task 11.1: the card's "Conflict" option, on for every product card:
  // the Conflict Message in the card body, above the price and action row (card 01). A card
  // that offers no Add or stepper (blocked) shows none, as it shows no basket error.
  const shownConflict = useLineConflictMessage(product.sku, {
    blocked: notOnAccount || supplierUnavailable,
  });
  const conflictAction = shownConflict?.action ?? null;
  const conflictMessage =
    shownConflict === null ? null : (
      <ConflictMessage
        message={shownConflict.message}
        {...(conflictAction === null
          ? {}
          : {
              action: {
                label: conflictAction.label,
                onClick: () => runConflictAction(conflictAction.intent),
              },
            })}
      />
    );

  // Inline basket message (a failed add or edit), full-width below
  // the footer so they never squash the CTA row.
  const inlineMessages = (
    <>
      {!notOnAccount && !supplierUnavailable && basketError && (
        <p className={styles.basketMessage} role="alert">
          {basketError}
        </p>
      )}
    </>
  );

  // Story 7.11 — in-card Substitutes sheet (anchored to `.card`). Mounted
  // whenever substitutes exist so the prototype's transitions can run; `open`
  // drives visibility/interactivity (aria-hidden when closed).
  const substitutesOverlay = hasSubstitutes && (
    <SubstitutesPanel
      productName={product.name}
      substitutes={substitutes}
      open={panelOpen}
      onClose={closeSubstitutes}
      suppressUpsell={suppressUpsell}
      pricedByHost
    />
  );
  // List card (2026-07-17 prototype parity): ≥768px the substitutes EXPAND
  // INLINE under the card content (prototype `substituteInlineBlock`); <768px
  // the slide-up overlay is used, same as the grid card. Both are mounted and
  // media-gated in CSS (`subInlineHost` / `subOverlayHost`), mirroring the
  // prototype's `hidden sm:block` / `sm:hidden` pair.
  const substitutesListBlocks = hasSubstitutes && (
    <>
      <div className={`${styles.listFullRow} ${styles.subInlineHost}`}>
        <SubstitutesPanel
          productName={product.name}
          substitutes={substitutes}
          open={panelOpen}
          variant="inline"
          onClose={closeSubstitutes}
          suppressUpsell={suppressUpsell}
          pricedByHost
        />
      </div>
      <div className={styles.subOverlayHost}>{substitutesOverlay}</div>
    </>
  );

  const favouriteErrorEl = favouriteError && (
    <p className={styles.favouriteError} role="alert">
      {favouriteError}
    </p>
  );

  const cardClass = [
    styles.card,
    layout === 'list' ? styles.listCard : styles.gridCard,
    notOnAccount ? styles.notOnAccount : '',
  ]
    .filter(Boolean)
    .join(' ');

  // ── List layout (Pricing migration 18, ADO-2249; Figma set 2, v9's list "for detail"): the
  // decision line (the heart, the state, the offer, then RSP, margin and case on the right),
  // then the image, the name and the codes line (code, stock pill, the badges, Stock card
  // and the `N in store` teaser), and the price and the action on the right. Below 768px the price
  // and action take their own row and the Stock card and teaser drop out, as today. ──
  if (layout === 'list') {
    return (
      <article className={cardClass} data-layout="list" data-state={cardState}>
        <div className={styles.decisionLine} data-decision-line="" data-state={cardState}>
          {heartButton}
          {stateText}
          {priceOffer ?? (narrowViewport ? null : wideDealStrip)}
          <p className={styles.listNumbers} data-meta-row="numbers">
            {rspAndMargin}
            {rspAndMargin !== null && caseValue !== null && <> {separator} </>}
            {caseValue}
          </p>
        </div>

        <div className={styles.listContent}>
          <div className={styles.listImage}>
            <ProductImage src={product.imageUrl} alt={product.name} priority={priority} />
          </div>
          <div className={styles.listMiddle}>
            <h3 className={styles.listTitle}>{productLink(styles.nameLink)}</h3>
            <div className={styles.codesLine} data-codes-line="">
              <span className={styles.sku}>{product.sku}</span>
              {stockPill}
              {hasBadges && badgeEls}
              {showsStockRow && stockButton}
              {showsStockRow && stockTeaser}
            </div>
            {featuredContent}
            {conflictMessage}
            {favouriteErrorEl}
          </div>
          {/* m/02: on a phone the multi-buy strip is the compact one, on its own row after the
              codes line and before the price and the action. */}
          {priceOffer === null && narrowViewport && compactDealStrip !== null && (
            <div className={styles.listDealRow}>{compactDealStrip}</div>
          )}
          {footer}
          {rbpLinksRow}
        </div>

        {inlineMessages}
        {substitutesListBlocks}
        {showsStockRow && stockCardPopover}
      </article>
    );
  }

  // ── Grid layout (Pricing migration 18, ADO-2249; Figma set 2, v9's grid "for speed", 419px):
  // the top strip when the product is in the cart or on order (the heart at its right end),
  // else the top row with the heart; the badge row; the image; the code row, the name and the
  // offer slot; then `RSP · Margin` and `Case`, the conflict message when there is one (Story
  // 8.3, DEC-12 item 4: above the divider over the footer, growing the card), the divider and
  // the footer. ──
  return (
    <article
      className={cardClass}
      data-layout="grid"
      data-name-row={nameRow}
      data-state={cardState}
    >
      {cardState !== undefined && (
        <div className={styles.topStrip} data-top-strip={cardState}>
          {stateText}
          {heartButton}
        </div>
      )}
      <div className={styles.topRow} data-top-row="">
        <div className={styles.badges} data-badges="">
          {badgeEls}
        </div>
        {cardState === undefined && heartButton}
      </div>
      <div className={styles.cardImage}>
        <ProductImage src={product.imageUrl} alt={product.name} priority={priority} />
      </div>

      <div className={styles.lower} data-card-lower="">
        <div className={styles.headerRow}>
          <span className={styles.sku}>{product.sku}</span>
          {stockPill}
        </div>
        <div
          className={styles.nameRow}
          onPointerEnter={nameTruncated ? placeNameTooltip : undefined}
          onFocus={nameTruncated ? placeNameTooltip : undefined}
        >
          <h3 ref={nameRef} className={styles.name}>
            {productLink(styles.nameLink)}
          </h3>
          {nameTruncated && (
            <span
              ref={nameTooltipRef}
              className={styles.nameTooltip}
              aria-hidden="true"
              data-name-tooltip=""
              data-placement={nameTooltipSide}
            >
              {product.name.trim()}
            </span>
          )}
        </div>
        {featuredContent}
        {/* Pinned to the lower block's foot, 5px above the divider, so a row's offer rows and pills
            line up whatever the names above them; spare height goes above it. */}
        {offerSlot === null ? null : (
          <div className={styles.offerSlot} data-offer-slot="">
            {offerSlot}
          </div>
        )}
      </div>

      <div className={styles.pricing}>
        <div className={styles.numbers}>
          <p className={styles.metaRow} data-meta-row="rsp">
            {rspAndMargin}
          </p>
          <p className={styles.metaRow} data-meta-row="case">
            {caseValue}
          </p>
        </div>
        {conflictMessage}
        {favouriteErrorEl}
        <div className={styles.footerDivider} data-footer-divider="" aria-hidden="true" />
        {footer}
        {rbpLinksRow}
      </div>
      {inlineMessages}
      {substitutesOverlay}
    </article>
  );
}
